/**
 * @fileoverview Tasks page — the TODO Funnel's home.
 * @module components/TaskCanvas/TasksPage
 *
 * Phase 4 (docs/TODO-FUNNEL-VISION.md §3.4): the bubble canvas is the
 * default playground; the Phase-0 kanban stays as the "Board" view. The
 * triage review strip sits on top in both views.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Circle, CalendarRange, Table2, History, Waves, Target, Inbox, Gauge } from 'lucide-react';
import { TaskBubbleCanvas, TaskFlow, ALL_TIME_HOURS, TaskMatrix, TaskWeekBoard, TaskFilterBar, GoalsPanel, ReviewQueue, TodayBar, PipelinePlayground, applyTaskFilter, DEFAULT_TASK_FILTER } from '@techstack/ui';
import apiClient from '../../lib/apiClient';
import { toast } from '../Toast';
import { useRole } from '../../hooks/useRole';
import ApprovalQueue from './ApprovalQueue';
import './TaskCanvas.css';
import '@techstack/ui/dist/styles/task-canvas.css';

/**
 * Run a canvas mutation and SAY when it fails (adversarial review §1.2): the
 * goals board must never swallow an error into a blank state. Rethrows so
 * GoalCanvas's own catch keeps the user's typed input.
 */
async function surfacing(message, fn) {
  try {
    return await fn();
  } catch (e) {
    toast(message, 'error');
    throw e;
  }
}

/** Optimistically created rows carry a tmp- id until the refetch swaps in
 *  the real one; mutations against them are dropped (sub-second window). */
const isTmp = (id) => String(id).startsWith('tmp-');

/** Views that render the (capped) task list — the only ones the cap note
 *  is about. */
const LIST_VIEWS = new Set(['bubbles', 'week', 'matrix']);

function TasksPage() {
  const [view, setView] = useState('bubbles');
  const [showDone, setShowDone] = useState(false);
  const [flowWindow, setFlowWindow] = useState(24 * 7);
  // ONE filter bar across all views (Ryan, 2026-08-07): search, lenses,
  // and group dropdowns filter whichever view is active.
  const [filter, setFilter] = useState(DEFAULT_TASK_FILTER);
  const queryClient = useQueryClient();

  // Boards as files. The chosen board is remembered per browser so the app
  // opens where you left it; the server picks the most recent otherwise.
  const [boardId, setBoardId] = useState(() => {
    try { return window.localStorage.getItem('tekstak.boardId') || null; } catch { return null; }
  });
  const { data: boardsData } = useQuery({
    queryKey: ['tasks', 'boards'],
    queryFn: () => apiClient.get('/api/v1/tasks/boards'),
    enabled: view === 'goals',
  });
  const boards = useMemo(() => boardsData?.boards || [], [boardsData]);
  // Fall back to the most recently opened board when nothing is chosen, or
  // when the chosen one has been archived away.
  const activeBoardId = useMemo(() => {
    if (boardId && boards.some((b) => b.id === boardId)) return boardId;
    return boards[0]?.id || null;
  }, [boardId, boards]);
  const invalidateBoards = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['tasks', 'boards'] }),
    [queryClient]
  );
  const openBoard = useCallback((id) => surfacing("Couldn't open that board", async () => {
    setBoardId(id);
    try { window.localStorage.setItem('tekstak.boardId', id); } catch { /* best effort */ }
    // Everything on screen belongs to a board, so all four reads refresh.
    queryClient.invalidateQueries({ queryKey: ['tasks', 'goals'] });
    queryClient.invalidateQueries({ queryKey: ['tasks', 'goal-notes'] });
    queryClient.invalidateQueries({ queryKey: ['tasks', 'canvas'] });
    await apiClient.patch(`/api/v1/tasks/boards/${id}`, { opened: true });
    invalidateBoards();
  }), [queryClient, invalidateBoards]);
  const createBoard = useCallback((draft) => surfacing("Couldn't create the board", async () => {
    const res = await apiClient.post('/api/v1/tasks/boards', draft);
    invalidateBoards();
    if (res?.board?.id) await openBoard(res.board.id);
  }), [invalidateBoards, openBoard]);
  const patchBoard = useCallback((id, updates, label) => surfacing(label, async () => {
    await apiClient.patch(`/api/v1/tasks/boards/${id}`, updates);
    invalidateBoards();
  }), [invalidateBoards]);
  const renameBoard = useCallback((id, name) => patchBoard(id, { name }, "Couldn't rename the board"), [patchBoard]);
  const retagBoard = useCallback((id, account) => patchBoard(id, { account }, "Couldn't retag the board"), [patchBoard]);
  const archiveBoard = useCallback((id) => patchBoard(id, { archived: true }, "Couldn't archive the board"), [patchBoard]);


  const { data: taskData } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => apiClient.get('/api/v1/tasks?status=active&order=board&limit=500'),
    staleTime: 15_000,
  });

  // R2: the today contract + focus mode. While a focus block runs, every
  // board narrows to proactive work — the block is an environment, not a
  // calendar entry.
  const [focusActive, setFocusActive] = useState(false);
  const { data: todayData, isLoading: todayLoading } = useQuery({
    queryKey: ['tasks', 'today'],
    queryFn: () => apiClient.get('/api/v1/tasks/today'),
    staleTime: 60_000,
  });
  const handleSetToday = useCallback(async (taskIds) => {
    const res = await apiClient.put('/api/v1/tasks/today', { task_ids: taskIds });
    queryClient.invalidateQueries({ queryKey: ['tasks', 'today'] });
    return res;
  }, [queryClient]);

  // R1: the ONE review queue — polled lightly so the badge stays honest
  // even when the view is closed; disposition invalidates it.
  const { data: reviewData, isLoading: reviewLoading } = useQuery({
    queryKey: ['tasks', 'review'],
    queryFn: () => apiClient.get('/api/v1/tasks/review'),
    staleTime: 30_000,
    refetchInterval: 120_000,
  });

  // Two tiers (UX review 2026-08-17): most dispositions only change the
  // queue itself — refetching every board per keystroke made rapid triage a
  // refetch storm. Actions that move TASKS still invalidate the world.
  const invalidateReview = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['tasks', 'review'] });
  }, [queryClient]);
  const invalidateReviewAndBoards = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  }, [queryClient]);

  const reviewHandlers = useMemo(() => ({
    onAcceptTriage: async (taskId) => {
      await apiClient.post('/api/v1/tasks/accept-triage', { ids: [taskId] });
      invalidateReviewAndBoards();
    },
    onKeepAsIs: async (taskId) => {
      await apiClient.patch(`/api/v1/tasks/${taskId}`, { triage_state: 'confirmed' });
      invalidateReviewAndBoards();
    },
    onSetOrigin: async (taskId, origin) => {
      await apiClient.patch(`/api/v1/tasks/${taskId}`, { origin });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'review'] });
    },
    // `keep` names the surviving side — the human's pick, not just the
    // machine's suggestion (the endpoint has always accepted it).
    onResolveDuplicate: async (pairId, action, keep) => {
      await apiClient.post(`/api/v1/tasks/duplicates/${pairId}/resolve`,
        keep ? { action, keep } : { action });
      invalidateReviewAndBoards();
    },
    // One call for the whole batch — the endpoint already takes ids[].
    onBulkAcceptTriage: async (ids) => {
      await apiClient.post('/api/v1/tasks/accept-triage', { ids });
      invalidateReviewAndBoards();
    },
    onGroup: async (cluster, title) => {
      await apiClient.post('/api/v1/tasks/group-cluster', {
        task_ids: cluster.task_ids,
        title,
        correlation: { basis: cluster.basis, origin: cluster.origin, avg_similarity: cluster.avg_similarity, min_similarity: cluster.min_similarity },
      });
      invalidateReviewAndBoards();
    },
    onDismissGroup: async (cluster) => {
      await apiClient.post('/api/v1/tasks/group-candidates/dismiss', {
        task_ids: cluster.task_ids,
        basis: cluster.basis,
      });
      invalidateReview();
    },
    onApprove: async (workItemId) => {
      await apiClient.post(`/api/v1/work-items/${workItemId}/approve`, {});
      invalidateReview();
    },
    // The human's NO — same perimeter as approve (human JWT only).
    onDeclineWorkItem: async (workItemId) => {
      await apiClient.post(`/api/v1/work-items/${workItemId}/decline`, {});
      invalidateReview();
    },
    // R4: registry challenges — accept applies the suggested status through
    // the registry's own PATCH; keep resets the challenge window.
    onAcceptChallenge: async (ch) => {
      const path = ch.registry === 'sync' ? 'syncs' : ch.registry === 'coverage' ? 'coverage' : 'pursuits';
      await apiClient.patch(`/api/v1/tasks/${path}/${ch.id}`, { status: ch.suggestion });
      invalidateReview();
    },
    onKeepRegistry: async (ch) => {
      await apiClient.post('/api/v1/tasks/registry-keep', { registry: ch.registry, id: ch.id });
      invalidateReview();
    },
    onRetryWorkItem: async (id) => {
      await apiClient.post(`/api/v1/work-items/${id}/retry`, {});
      invalidateReview();
    },
    onRetireWorkItem: async (id) => {
      await apiClient.post(`/api/v1/work-items/${id}/retire`, {});
      invalidateReview();
    },
    // The ritual: record it, then open the Goals HQ — the review IS the
    // Goals view with the ritual card as its door.
    // Cases (S5): accept-all / dismiss are dispositions and refresh every
    // board (members move); promote and add-as-task keep the row and light
    // locally inside ReviewQueue, so only the review query refreshes.
    onReviewCase: async (caseId) => {
      await apiClient.post(`/api/v1/tasks/cases/${caseId}/review`, {});
      invalidateReviewAndBoards();
    },
    onDismissCase: async (caseId) => {
      await apiClient.post(`/api/v1/tasks/cases/${caseId}/dismiss`, {});
      invalidateReviewAndBoards();
    },
    onPromoteCase: async (caseId) => {
      await apiClient.post(`/api/v1/tasks/cases/${caseId}/promote`, {});
      invalidateReviewAndBoards();
    },
    onAcceptCaseNextAction: async (caseId, index) => {
      await apiClient.post(`/api/v1/tasks/cases/${caseId}/next-actions/${index}/accept`, {});
      invalidateReviewAndBoards();
    },
    onStartWeeklyReview: async () => {
      await apiClient.post('/api/v1/tasks/weekly-review', {});
      setView('goals');
      invalidateReview();
    },
    onSkipWeeklyReview: async () => {
      await apiClient.post('/api/v1/tasks/weekly-review', { skipped: true });
      invalidateReview();
    },
    // The Friday close (vision §6): record the bookend, then look at the
    // goals the next week serves.
    onCloseWeek: async () => {
      await apiClient.post('/api/v1/tasks/weekly-review', { kind: 'close' });
      setView('goals');
      invalidateReview();
    },
    onSkipCloseWeek: async () => {
      await apiClient.post('/api/v1/tasks/weekly-review', { kind: 'close', skipped: true });
      invalidateReview();
    },
  }), [invalidateReview, invalidateReviewAndBoards, queryClient]);

  // Goals (Wave G0) — outcomes + the allocation strip. Fetched only while
  // the Goals view is open; the task pool above supplies per-goal tasks.
  const { data: goalsData, isLoading: goalsLoading, isError: goalsFailed, refetch: refetchGoals } = useQuery({
    queryKey: ['tasks', 'goals', activeBoardId],
    queryFn: () => apiClient.get(`/api/v1/tasks/goals?status=all${activeBoardId ? `&board=${activeBoardId}` : ''}`),
    // The Number's deal console links deals to goals — both views need them.
    enabled: view === 'goals' || view === 'number',
    staleTime: 30_000,
  });
  const { data: allocationData } = useQuery({
    queryKey: ['tasks', 'allocation'],
    queryFn: () => apiClient.get('/api/v1/tasks/allocation?days=7'),
    enabled: view === 'goals',
    staleTime: 60_000,
  });

  // Canvas mutations are OPTIMISTIC (repo standard; review §2.2): patch the
  // cache in the same frame as the gesture, persist behind, roll back +
  // toast on failure, and settle with an invalidate.
  const patchGoals = useCallback((mut) => {
    const key = ['tasks', 'goals', activeBoardId];
    const prev = queryClient.getQueryData(key);
    queryClient.setQueryData(key, (cur) =>
      (cur ? { ...cur, goals: mut(cur.goals || []) } : cur));
    return () => queryClient.setQueryData(key, prev);
  }, [queryClient, activeBoardId]);

  const handleCreateGoal = useCallback((draft) => surfacing("Couldn't create the goal", async () => {
    const tmpId = `tmp-${Date.now()}`;
    const rollback = patchGoals((goals) => [...goals, {
      id: tmpId, outcome: draft.outcome, status: 'active', priority: 0,
      canvas_x: draft.canvas_x ?? null, canvas_y: draft.canvas_y ?? null,
      people: [], task_count: 0,
    }]);
    try {
      await apiClient.post('/api/v1/tasks/goals', {
        board_id: activeBoardId,
        outcome: draft.outcome,
        why: draft.why,
        horizon: draft.horizon,
        // Name/acronym as typed — the backend resolves it with the same
        // resolver capture uses.
        account: draft.account || null,
        canvas_x: draft.canvas_x,
        canvas_y: draft.canvas_y,
      });
    } catch (e) {
      rollback();
      throw e;
    }
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  }), [queryClient, patchGoals]);

  const handleUpdateGoalStatus = useCallback((goalId, status) => surfacing("Couldn't change the goal's status", async () => {
    if (isTmp(goalId)) return;
    const rollback = patchGoals((goals) => goals.map((g) => (g.id === goalId ? { ...g, status } : g)));
    try {
      await apiClient.patch(`/api/v1/tasks/goals/${goalId}`, { status });
    } catch (e) {
      rollback();
      throw e;
    }
    queryClient.invalidateQueries({ queryKey: ['tasks', 'goals'] });
  }), [queryClient, patchGoals]);

  // Canvas facet edits (SMART whiteboard) — same PATCH, any field.
  const handleUpdateGoal = useCallback((goalId, updates) => surfacing("Couldn't save the goal edit", async () => {
    if (isTmp(goalId)) return;
    const rollback = patchGoals((goals) => goals.map((g) => (g.id === goalId ? { ...g, ...updates } : g)));
    try {
      await apiClient.patch(`/api/v1/tasks/goals/${goalId}`, updates);
    } catch (e) {
      rollback();
      throw e;
    }
    queryClient.invalidateQueries({ queryKey: ['tasks', 'goals'] });
  }), [queryClient, patchGoals]);

  // The brainstorm layer (GOAL-CANVAS-VISION §2): thought stickies.
  const { data: notesData } = useQuery({
    queryKey: ['tasks', 'goal-notes', activeBoardId],
    queryFn: () => apiClient.get(`/api/v1/tasks/goal-notes${activeBoardId ? `?board=${activeBoardId}` : ''}`),
    enabled: view === 'goals',
    staleTime: 30_000,
  });
  const invalidateNotes = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['tasks', 'goal-notes'] }),
    [queryClient]
  );
  const patchNotes = useCallback((mut) => {
    const key = ['tasks', 'goal-notes', activeBoardId];
    const prev = queryClient.getQueryData(key);
    queryClient.setQueryData(key, (cur) =>
      (cur ? { ...cur, notes: mut(cur.notes || []) } : cur));
    return () => queryClient.setQueryData(key, prev);
  }, [queryClient, activeBoardId]);

  const handleCreateNote = useCallback((draft) => surfacing("Couldn't throw the thought", async () => {
    const rollback = patchNotes((notes) => [...notes, {
      id: `tmp-${Date.now()}`, text: draft.text,
      canvas_x: draft.canvas_x ?? null, canvas_y: draft.canvas_y ?? null,
      goal_id: draft.goal_id ?? null, walk_order: draft.walk_order ?? null,
      color: draft.color ?? null,
    }]);
    try {
      await apiClient.post('/api/v1/tasks/goal-notes', { ...draft, board_id: activeBoardId });
    } catch (e) {
      rollback();
      throw e;
    }
    invalidateNotes();
  }), [invalidateNotes, patchNotes]);
  const handleUpdateNote = useCallback((noteId, updates) => surfacing("Couldn't save the thought", async () => {
    if (isTmp(noteId)) return;
    const rollback = patchNotes((notes) => notes.map((n) => (n.id === noteId ? { ...n, ...updates } : n)));
    try {
      await apiClient.patch(`/api/v1/tasks/goal-notes/${noteId}`, updates);
    } catch (e) {
      rollback();
      throw e;
    }
    invalidateNotes();
  }), [invalidateNotes, patchNotes]);
  const handleDeleteNote = useCallback((noteId) => surfacing("Couldn't delete the thought", async () => {
    const rollback = patchNotes((notes) => notes.filter((n) => n.id !== noteId));
    if (isTmp(noteId)) return;
    try {
      await apiClient.del(`/api/v1/tasks/goal-notes/${noteId}`);
    } catch (e) {
      rollback();
      throw e;
    }
    invalidateNotes();
  }), [invalidateNotes, patchNotes]);

  // The Number (vision §4): quota thermometer + deal playground.
  const [numberFy, setNumberFy] = useState(null);
  const { data: pipelineData, isLoading: pipelineLoading, isError: pipelineFailed, refetch: refetchPipeline } = useQuery({
    queryKey: ['tasks', 'pipeline', numberFy],
    queryFn: () => apiClient.get('/api/v1/tasks/pipeline', { params: numberFy ? { fy: numberFy } : {} }),
    // The goals canvas docks the Number (vision §4: "always visible").
    enabled: view === 'number' || view === 'goals',
    staleTime: 15_000,
  });
  const invalidatePipeline = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['tasks', 'pipeline'] }),
    [queryClient]
  );
  const handleSetQuota = useCallback((fy, amount) => surfacing("Couldn't save the quota", async () => {
    await apiClient.put('/api/v1/tasks/pipeline/quota', { fy, amount });
    invalidatePipeline();
  }), [invalidatePipeline]);
  const handleCreateDeal = useCallback((draft) => surfacing("Couldn't create the deal", async () => {
    await apiClient.post('/api/v1/tasks/pipeline/deals', draft);
    invalidatePipeline();
  }), [invalidatePipeline]);
  // Drag-drop and slider releases must land INSTANTLY — patch the cache,
  // persist behind, truth-up after.
  const handleUpdateDeal = useCallback(async (dealId, updates) => {
    queryClient.setQueryData(['tasks', 'pipeline', numberFy], (prev) => {
      if (!prev?.deals) return prev;
      return { ...prev, deals: prev.deals.map((d) => (d.id === dealId ? { ...d, ...updates } : d)) };
    });
    await apiClient.patch(`/api/v1/tasks/pipeline/deals/${dealId}`, updates)
      .then(() => {
        invalidatePipeline();
        // Linking a deal changes the goal cards' $ chips and rank inputs.
        if (updates.goal_id !== undefined) queryClient.invalidateQueries({ queryKey: ['tasks', 'goals'] });
      })
      .catch((e) => { invalidatePipeline(); throw e; });
  }, [queryClient, numberFy, invalidatePipeline]);
  const handleDeleteDeal = useCallback((dealId) => surfacing("Couldn't delete the deal", async () => {
    await apiClient.del(`/api/v1/tasks/pipeline/deals/${dealId}`);
    invalidatePipeline();
  }), [invalidatePipeline]);

  // On-demand decomposition (vision §5) — brainstorm-fed, runs NOW. Success
  // gets said out loud too: the steps land in another view, so without the
  // toast the click looks like a no-op (review §2.6).
  // The drawing layer (S3): regions and free connectors, fetched together
  // so a connector never paints before the thing it points at.
  const { data: canvasData } = useQuery({
    queryKey: ['tasks', 'canvas', activeBoardId],
    queryFn: () => apiClient.get(`/api/v1/tasks/canvas${activeBoardId ? `?board=${activeBoardId}` : ''}`),
    enabled: view === 'goals',
  });
  const invalidateCanvas = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['tasks', 'canvas'] }),
    [queryClient]
  );
  const patchCanvas = useCallback((key, mut) => {
    const qk = ['tasks', 'canvas', activeBoardId];
    const prev = queryClient.getQueryData(qk);
    queryClient.setQueryData(qk, (cur) =>
      (cur ? { ...cur, [key]: mut(cur[key] || []) } : cur));
    return () => queryClient.setQueryData(qk, prev);
  }, [queryClient, activeBoardId]);

  const handleCreateObject = useCallback((draft) => surfacing("Couldn't draw the region", async () => {
    const res = await apiClient.post('/api/v1/tasks/canvas/objects', { ...draft, board_id: activeBoardId });
    invalidateCanvas();
    return res?.object;
  }), [invalidateCanvas]);

  const handleUpdateObject = useCallback((id, updates) => surfacing("Couldn't save the region", async () => {
    if (isTmp(id)) return;
    const rollback = patchCanvas('objects', (os) => os.map((o) => (o.id === id ? { ...o, ...updates } : o)));
    try {
      await apiClient.patch(`/api/v1/tasks/canvas/objects/${id}`, updates);
    } catch (e) {
      rollback();
      throw e;
    }
    invalidateCanvas();
  }), [invalidateCanvas, patchCanvas]);

  const handleDeleteObject = useCallback((id) => surfacing("Couldn't remove the region", async () => {
    const rollback = patchCanvas('objects', (os) => os.filter((o) => o.id !== id));
    try {
      await apiClient.del(`/api/v1/tasks/canvas/objects/${id}`);
    } catch (e) {
      rollback();
      throw e;
    }
    invalidateCanvas();
  }), [invalidateCanvas, patchCanvas]);

  const handleCreateEdge = useCallback((draft) => surfacing("Couldn't draw the connection", async () => {
    await apiClient.post('/api/v1/tasks/canvas/edges', { ...draft, board_id: activeBoardId });
    invalidateCanvas();
  }), [invalidateCanvas]);

  const handleUpdateEdge = useCallback((id, updates) => surfacing("Couldn't save the connection", async () => {
    const rollback = patchCanvas('edges', (es) => es.map((e) => (e.id === id ? { ...e, ...updates } : e)));
    try {
      await apiClient.patch(`/api/v1/tasks/canvas/edges/${id}`, updates);
    } catch (e) {
      rollback();
      throw e;
    }
    invalidateCanvas();
  }), [invalidateCanvas, patchCanvas]);

  const handleDeleteEdge = useCallback((id) => surfacing("Couldn't remove the connection", async () => {
    const rollback = patchCanvas('edges', (es) => es.filter((e) => e.id !== id));
    try {
      await apiClient.del(`/api/v1/tasks/canvas/edges/${id}`);
    } catch (e) {
      rollback();
      throw e;
    }
    invalidateCanvas();
  }), [invalidateCanvas, patchCanvas]);

  // A photograph of a real whiteboard, laid under the board (photo P0).
  // The browser rectified it, so what lands is exactly what was approved.
  // TekStak only: apps/gas/CLAUDE.md carve-out #7 keeps uploads off GAS.
  const importPhoto = useCallback((payload) => surfacing("Couldn't add that photo", async () => {
    // Land it at the board's own scale: a sticky is 200 world px wide, and
    // a whiteboard is roughly ten stickies across.
    const worldW = 2000;
    const worldH = Math.max(1, Math.round(worldW * (payload.px_h / Math.max(1, payload.px_w))));
    await apiClient.post('/api/v1/tasks/canvas/images', {
      board_id: activeBoardId,
      data_base64: payload.data_base64,
      content_type: payload.content_type,
      x: 0,
      y: 0,
      w: worldW,
      h: worldH,
      opacity: 0.35,
    });
    invalidateCanvas();
  }), [activeBoardId, invalidateCanvas]);

  // Reading the stickies off that photo (photo P1). The read PROPOSES; the
  // commit writes only the lines the human ticked. The photograph stays
  // underneath either way, which is what makes a misread checkable.
  const readPhoto = useCallback((objectId) => surfacing("Couldn't read that photo", async () => (
    apiClient.post(`/api/v1/tasks/canvas/images/${objectId}/read`, {})
  )), []);

  const commitStickies = useCallback((objectId, payload) => surfacing("Couldn't add those stickies", async () => {
    const res = await apiClient.post(`/api/v1/tasks/canvas/images/${objectId}/commit`, {
      board_id: activeBoardId, ...payload,
    });
    invalidateNotes();
    // P2 also creates regions and the connectors between the new stickies,
    // and those live in the canvas query, not the notes one.
    invalidateCanvas();
    return res;
  }), [activeBoardId, invalidateNotes, invalidateCanvas]);

  // The two verbs on a region (S5). Polish applies to the board; Process
  // only ever proposes, and nothing is written until the human ticks.
  const polishRegion = useCallback((regionId, payload) => surfacing("Couldn't polish that region", async () => {
    const res = await apiClient.post(`/api/v1/tasks/canvas/regions/${regionId}/polish`, {
      board_id: activeBoardId, ...payload,
    });
    invalidateNotes();
    invalidateCanvas();
    return res;
  }), [activeBoardId, invalidateNotes, invalidateCanvas]);

  const processRegion = useCallback((regionId, payload) => surfacing("Couldn't read that region", async () => (
    apiClient.post(`/api/v1/tasks/canvas/regions/${regionId}/process`, { board_id: activeBoardId, ...payload })
  )), [activeBoardId]);

  const commitRegion = useCallback((regionId, payload) => surfacing("Couldn't create the action items", async () => {
    const res = await apiClient.post(`/api/v1/tasks/canvas/regions/${regionId}/commit`, {
      board_id: activeBoardId, ...payload,
    });
    // New tasks land in the funnel and against their goal.
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    return res;
  }), [activeBoardId, queryClient]);

  // A group drag persists in ONE request. Optimistic first: the cards are
  // already where the pointer left them, so the patch is just bookkeeping.
  const handleMoveMany = useCallback((moves) => surfacing("Couldn't save the move", async () => {
    const real = (moves || []).filter((m) => !isTmp(m.id));
    if (!real.length) return;
    const byId = new Map(real.map((m) => [m.id, m]));
    const rollbackGoals = patchGoals((goals) => goals.map((g) => (byId.has(g.id)
      ? { ...g, canvas_x: byId.get(g.id).canvas_x, canvas_y: byId.get(g.id).canvas_y } : g)));
    const rollbackNotes = patchNotes((notes) => notes.map((n) => (byId.has(n.id)
      ? { ...n, canvas_x: byId.get(n.id).canvas_x, canvas_y: byId.get(n.id).canvas_y } : n)));
    try {
      await apiClient.patch('/api/v1/tasks/canvas/positions', { moves: real });
    } catch (e) {
      rollbackGoals();
      rollbackNotes();
      throw e;
    }
    queryClient.invalidateQueries({ queryKey: ['tasks', 'goals'] });
    invalidateNotes();
  }), [queryClient, patchGoals, patchNotes, invalidateNotes]);

  const handleDecomposeGoal = useCallback((goalId) => surfacing("Couldn't decompose — no plan could be built from this goal yet", async () => {
    if (isTmp(goalId)) return;
    const res = await apiClient.post(`/api/v1/tasks/goals/${goalId}/decompose`, {});
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    if (res?.step_count) {
      toast((res.kept > 0 || res.replaced > 0)
        ? `Plan re-drawn — ${res.kept} done step${res.kept === 1 ? '' : 's'} kept, ${res.step_count} fresh`
        : res.source === 'brainstorm'
          ? `Plan created — your ${res.step_count} thoughts became the steps`
          : `Plan created — ${res.step_count} steps`);
    }
  }), [queryClient]);

  // Walk-back assist (vision §5): a draft suggestion for the chain's tail —
  // it only prefills the canvas input; nothing persists until Ryan commits.
  const handleSuggestWalkBack = useCallback((goalId) => surfacing("Couldn't fetch a suggestion — write the step yourself", async () => {
    if (isTmp(goalId)) return null;
    const res = await apiClient.post(`/api/v1/tasks/goals/${goalId}/walkback`, {});
    if (!res?.suggestion) {
      toast(res?.reason === 'capped' ? 'Goal-AI budget is spent for today — write the step yourself'
        : 'No suggestion available — write the step yourself', 'info');
      return null;
    }
    return res.suggestion;
  }), []);

  const handleDecompose = useCallback(async (taskId) => {
    await apiClient.post(`/api/v1/tasks/${taskId}/decompose`, {});
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  }, [queryClient]);

  // Coverage (Wave G2 §4.7) — relationship cadences behind the Goals view.
  const { data: coverageData } = useQuery({
    queryKey: ['tasks', 'coverage'],
    queryFn: () => apiClient.get('/api/v1/tasks/coverage?status=all'),
    enabled: view === 'goals',
    staleTime: 60_000,
  });

  const handleAddCoverage = useCallback((draft) => surfacing("Couldn't add the coverage target", async () => {
    await apiClient.post('/api/v1/tasks/coverage', {
      contact_email: draft.contact_email,
      tier: draft.tier,
      why: draft.why,
    });
    queryClient.invalidateQueries({ queryKey: ['tasks', 'coverage'] });
  }), [queryClient]);

  const handleUpdateCoverage = useCallback((targetId, updates) => surfacing("Couldn't save the coverage target", async () => {
    await apiClient.patch(`/api/v1/tasks/coverage/${targetId}`, updates);
    queryClient.invalidateQueries({ queryKey: ['tasks', 'coverage'] });
  }), [queryClient]);

  // Recurring syncs (Wave G2 §4.8) — the forward-agenda registry.
  const { data: syncsData, isError: syncsFailed, refetch: refetchSyncs } = useQuery({
    queryKey: ['tasks', 'syncs'],
    queryFn: () => apiClient.get('/api/v1/tasks/syncs?status=all'),
    enabled: view === 'goals',
    staleTime: 60_000,
  });

  const handleAddSync = useCallback((draft) => surfacing("Couldn't add the sync", async () => {
    await apiClient.post('/api/v1/tasks/syncs', {
      name: draft.name,
      account: draft.account,
      cadence_days: draft.cadence_days,
      next_date: draft.next_date,
      goal_id: draft.goal_id || null,
    });
    queryClient.invalidateQueries({ queryKey: ['tasks', 'syncs'] });
  }), [queryClient]);

  const handleUpdateSync = useCallback((syncId, updates) => surfacing("Couldn't save the sync", async () => {
    await apiClient.patch(`/api/v1/tasks/syncs/${syncId}`, updates);
    queryClient.invalidateQueries({ queryKey: ['tasks', 'syncs'] });
  }), [queryClient]);

  // Pursuits (Wave G2 §4.9) — standing asks on the escalation ladder.
  const { data: pursuitsData } = useQuery({
    queryKey: ['tasks', 'pursuits'],
    queryFn: () => apiClient.get('/api/v1/tasks/pursuits?status=all'),
    enabled: view === 'goals',
    staleTime: 60_000,
  });

  const handleAddPursuit = useCallback((draft) => surfacing("Couldn't add the pursuit", async () => {
    await apiClient.post('/api/v1/tasks/pursuits', {
      contact_email: draft.contact_email,
      ask: draft.ask,
    });
    queryClient.invalidateQueries({ queryKey: ['tasks', 'pursuits'] });
  }), [queryClient]);

  const handleUpdatePursuit = useCallback((pursuitId, updates) => surfacing("Couldn't save the pursuit", async () => {
    await apiClient.patch(`/api/v1/tasks/pursuits/${pursuitId}`, updates);
    queryClient.invalidateQueries({ queryKey: ['tasks', 'pursuits'] });
  }), [queryClient]);

  // Flow view — the funnel's observability lane. Fetched only while open;
  // refetches on a slow cadence because feed health moves in minutes.
  const { data: flowData, isLoading: flowLoading } = useQuery({
    queryKey: ['funnel-flow', flowWindow],
    queryFn: () => apiClient.get('/api/v1/funnel/flow', { params: { windowHours: flowWindow } }),
    enabled: view === 'flow',
    staleTime: 60_000,
    refetchInterval: view === 'flow' ? 120_000 : false,
  });

  // Connector credential health for the flow view's check-connection
  // popovers. /api/connectors is admin-mounted, so this fetch — and every
  // connector affordance downstream of it — is gated on isAdmin: a hot link
  // that 403s or lands on a Settings section the viewer can't see is worse
  // than none. Reads DB state only (no upstream API calls), so the slow
  // refetch costs nothing against provider quotas.
  const { isAdmin } = useRole();
  const { data: connectorData } = useQuery({
    queryKey: ['connectors'],
    queryFn: () => apiClient.get('/api/connectors'),
    enabled: view === 'flow' && isAdmin,
    staleTime: 60_000,
    refetchInterval: view === 'flow' ? 120_000 : false,
  });
  const connectorMap = useMemo(() => {
    if (!connectorData?.connectors) return null;
    const m = {};
    for (const c of connectorData.connectors) m[c.key] = c;
    return m;
  }, [connectorData]);

  const handleConnectorOpen = useCallback((connectorKey) => {
    if (typeof window.openSettings === 'function') {
      window.openSettings('connections', { connector: connectorKey });
    }
  }, []);

  const handleConnectorTest = useCallback(
    (connectorKey) => apiClient.post(`/api/connectors/${connectorKey}/test`, {})
      .finally(() => queryClient.invalidateQueries({ queryKey: ['connectors'] })),
    [queryClient]
  );

  // Connect a lane FROM the map (connection surfaces, TASK-CONNECTIONS §2b):
  // OAuth connectors redirect straight into the consent flow; anything else
  // (key-paste, config) falls through to its focused Settings row. The
  // callback returns to Settings → Connections either way — the flow view
  // shows the lit lane on the next visit.
  const handleConnectorConnect = useCallback(async (connectorKey) => {
    try {
      const r = await apiClient.get(`/api/connectors/${connectorKey}/oauth/start`);
      if (r?.url) {
        window.location.href = r.url;
        return;
      }
    } catch (_e) {
      // No OAuth flow (404) or start failed — Settings has the paste form.
    }
    if (typeof window.openSettings === 'function') {
      window.openSettings('connections', { connector: connectorKey });
    }
  }, []);

  // Streams a connector could feed that have no lane in the flow payload
  // yet — the "+ Add stream" menu. Ghost lanes (declared, not_wired) are
  // already in the rail; this catches connectors whose lane won't exist
  // until their first run (e.g. the Google Drive watched folder).
  const streamCatalog = useMemo(() => {
    if (!connectorData?.connectors || !flowData?.feeds) return null;
    const laneKeys = new Set(flowData.feeds.map((f) => f.connectorKey).filter(Boolean));
    return connectorData.connectors
      .filter((c) => c.feedLane && !laneKeys.has(c.key) && !c.orphaned)
      .map((c) => ({
        connectorKey: c.key,
        label: c.displayName,
        connected: c.credentialsPresent === true,
      }));
  }, [connectorData, flowData]);

  // Completed history — fetched only when the History toggle is on; done
  // rows render struck-through, searchable, reopenable.
  const { data: doneData } = useQuery({
    queryKey: ['tasks-done'],
    queryFn: () => apiClient.get('/api/v1/tasks?status=completed&limit=300'),
    staleTime: 60_000,
    enabled: showDone,
  });
  // Archived shelf (vision doc §3.9) — fetched only under the Archived lens.
  const showArchived = filter.lensKey === 'archived';
  const { data: archivedData } = useQuery({
    queryKey: ['tasks-archived'],
    queryFn: () => apiClient.get('/api/v1/tasks?archived=1&limit=300'),
    staleTime: 60_000,
    enabled: showArchived,
  });
  // R2: today's picks stay visible on every board — the contract leaves
  // the bar. Stamped here so all three boards inherit the marker.
  const todayIds = useMemo(
    () => new Set((todayData?.plan?.tasks || []).map((t) => t.id)),
    [todayData]
  );
  const tasks = useMemo(
    () => [
      ...(taskData?.tasks || []),
      ...(showDone ? doneData?.tasks || [] : []),
      ...(showArchived ? archivedData?.tasks || [] : []),
    ].map((t) => (todayIds.has(t.id) ? { ...t, today: true } : t)),
    [taskData, doneData, showDone, archivedData, showArchived, todayIds]
  );
  const filtered = useMemo(() => {
    const base = applyTaskFilter(tasks, filter);
    // R2 focus mode: proactive only — self-directed or goal-linked.
    return focusActive
      ? base.filter((t) => t.origin === 'self' || !!t.goal_id)
      : base;
  }, [tasks, filter, focusActive]);
  // The bubble canvas applies the task filter itself (it owns that UI), so
  // focus mode must narrow its INPUT — the block covers the default view
  // too, not just week/matrix.
  const canvasTasks = useMemo(
    () => (focusActive ? tasks.filter((t) => t.origin === 'self' || !!t.goal_id) : tasks),
    [tasks, focusActive]
  );

  // REL-4 (audit): the 500-task fetch cap must never be silent — when the
  // active world is bigger than the page, say so.
  const activeTotal = (taskData?.counts?.pending || 0) + (taskData?.counts?.in_progress || 0);
  const fetchedActive = taskData?.tasks?.length || 0;
  const capped = taskData?.pagination?.hasMore && activeTotal > fetchedActive;

  // Outbound ledger: every copy/send is recorded (fire-and-forget).
  const handleLogEvents = useCallback(
    (events) => { apiClient.post('/api/v1/tasks/events', { events }).catch(() => {}); },
    []
  );

  // "Sharpen": AI reframes a title into ACTION FORMAT; the editor applies
  // the returned title through the normal undoable update path.
  const handleReframe = useCallback(
    (taskId) => apiClient.post(`/api/v1/tasks/${taskId}/reframe`).then((r) => r?.title || null).catch(() => null),
    []
  );

  // Account acronyms for the inline account picker (+acct on a bubble).
  const { data: deptData } = useQuery({
    queryKey: ['departments'],
    queryFn: () => apiClient.get('/api/departments'),
    staleTime: 10 * 60 * 1000,
  });
  const accountOptions = (Array.isArray(deptData) ? deptData : deptData?.departments || [])
    .map((d) => d.acronym || d.department_acronym)
    .filter(Boolean);

  // Selectable people (teammates + CRM contacts) for the person picker.
  const { data: peopleData } = useQuery({
    queryKey: ['task-people'],
    queryFn: () => apiClient.get('/api/v1/tasks/people'),
    staleTime: 10 * 60 * 1000,
  });
  const people = peopleData?.people || [];

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    queryClient.invalidateQueries({ queryKey: ['tasks-done'] });
    queryClient.invalidateQueries({ queryKey: ['tasks-archived'] });
    // Flow drill rows are a separate query — edits made through the drawer's
    // full editor must reconcile there too, not just on the boards.
    queryClient.invalidateQueries({ queryKey: ['funnel-drill'] });
    queryClient.invalidateQueries({ queryKey: ['funnel-flow'] });
  }, [queryClient]);

  // ── Optimistic cache layer (UI-responsiveness review 2026-08-08) ──────
  // The shared components paint edits instantly via their own overlay; this
  // layer keeps the react-query cache in step so nothing snaps back when a
  // refetch lands, and replaces the invalidate-3-keys-per-edit pattern with
  // one debounced truth-up once writes go quiet. A failed write invalidates
  // immediately so server truth is restored.
  const syncTimer = React.useRef(null);
  const invalidateSoon = useCallback(() => {
    if (syncTimer.current) clearTimeout(syncTimer.current);
    syncTimer.current = setTimeout(() => { syncTimer.current = null; invalidate(); }, 1200);
  }, [invalidate]);

  const patchTaskCache = useCallback((taskId, updates) => {
    queryClient.setQueryData(['tasks'], (prev) => {
      if (!prev || !Array.isArray(prev.tasks)) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map((t) => {
          if (t.id !== taskId) return t;
          const patched = { ...t };
          for (const k of Object.keys(updates || {})) {
            if (k === 'account') patched.account_label = String(updates.account || '').toUpperCase() || null;
            else patched[k] = updates[k];
          }
          return patched;
        }),
      };
    });
  }, [queryClient]);

  // Single-row endpoints return bare RETURNING* rows (no joins) — merge,
  // never replace, so account_label/context_count survive the reconcile.
  const reconcileRow = useCallback((row) => {
    if (!row || !row.id) return;
    queryClient.setQueryData(['tasks'], (prev) => {
      if (!prev || !Array.isArray(prev.tasks)) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map((t) => {
          if (t.id !== row.id) return t;
          const merged = { ...t, ...row };
          if (row.account_label == null && t.account_label != null) merged.account_label = t.account_label;
          if (row.context_count == null && t.context_count != null) merged.context_count = t.context_count;
          return merged;
        }),
      };
    });
  }, [queryClient]);

  const dropFromActive = useCallback((taskId) => {
    queryClient.setQueryData(['tasks'], (prev) => (
      prev && Array.isArray(prev.tasks) ? { ...prev, tasks: prev.tasks.filter((t) => t.id !== taskId) } : prev
    ));
  }, [queryClient]);

  const handleMove = useCallback(
    (taskId, updates) => {
      patchTaskCache(taskId, updates);
      return apiClient.put(`/api/v1/tasks/${taskId}`, updates)
        .then((r) => { reconcileRow(r?.task); invalidateSoon(); })
        .catch((e) => { invalidate(); throw e; });
    },
    [patchTaskCache, reconcileRow, invalidateSoon, invalidate]
  );

  // R2: the today plan renders from its own query — a complete must strike
  // the bar chip INSTANTLY, same standard as the boards. done recomputes
  // from statuses so the count cannot drift from the chips.
  const patchTodayCache = useCallback((taskId, updates) => {
    queryClient.setQueryData(['tasks', 'today'], (prev) => {
      if (!prev?.plan?.tasks?.some((t) => t.id === taskId)) return prev;
      const picks = prev.plan.tasks.map((t) => (t.id === taskId ? { ...t, ...updates } : t));
      return {
        ...prev,
        plan: { ...prev.plan, tasks: picks, done: picks.filter((t) => t.status === 'completed').length },
      };
    });
  }, [queryClient]);

  const handleComplete = useCallback(
    (taskId) => {
      patchTaskCache(taskId, { status: 'completed', completed_at: new Date().toISOString() });
      patchTodayCache(taskId, { status: 'completed', completed_at: new Date().toISOString() });
      return apiClient.post(`/api/v1/tasks/${taskId}/complete`)
        .then(() => { dropFromActive(taskId); invalidateSoon(); })
        .catch((e) => { invalidate(); throw e; });
    },
    [patchTaskCache, patchTodayCache, dropFromActive, invalidateSoon, invalidate]
  );

  const handleReopen = useCallback(
    (taskId) => {
      patchTaskCache(taskId, { status: 'pending', completed_at: null });
      patchTodayCache(taskId, { status: 'pending', completed_at: null });
      return apiClient.post(`/api/v1/tasks/${taskId}/reopen`)
        .then((r) => { reconcileRow(r?.task); invalidate(); }) // done-list membership changed — full truth-up
        .catch((e) => { invalidate(); throw e; });
    },
    [patchTaskCache, patchTodayCache, reconcileRow, invalidate]
  );

  // INT-4 (audit): one transactional backend op — no more stranded
  // half-projects when the second write fails, and cycles are rejected.
  const handleGroup = useCallback(
    (taskId, targetTaskId) => {
      patchTaskCache(taskId, { project_id: targetTaskId });
      return apiClient.post(`/api/v1/tasks/${taskId}/group`, { parent_id: targetTaskId })
        .then((r) => { reconcileRow(r?.child); reconcileRow(r?.parent); invalidateSoon(); })
        .catch((e) => { invalidate(); throw e; });
    },
    [patchTaskCache, reconcileRow, invalidateSoon, invalidate]
  );

  // Archiving (vision doc §3.9): shelve / restore. The component hides
  // cards optimistically the instant they're clicked — these just persist
  // and refresh in the background (a rejection puts the card back).
  const handleArchive = useCallback(
    (taskId) => {
      return apiClient.post(`/api/v1/tasks/${taskId}/archive`)
        .then(() => { dropFromActive(taskId); invalidateSoon(); })
        .catch((e) => { invalidate(); throw e; });
    },
    [dropFromActive, invalidateSoon, invalidate]
  );

  const handleUnarchive = useCallback(
    (taskId) => apiClient.post(`/api/v1/tasks/${taskId}/unarchive`)
      .then(invalidate) // row changes list membership — full truth-up
      .catch((e) => { invalidate(); throw e; }),
    [invalidate]
  );

  // Bulk archive: ONE batched request for the whole selection (Ryan
  // 2026-08-07 — never N per-task round-trips from the bulk bar).
  const handleBulkArchive = useCallback(
    (ids) => {
      return apiClient.post('/api/v1/tasks/bulk-archive', { ids, archived: true })
        .then(() => { ids.forEach(dropFromActive); invalidateSoon(); })
        .catch((e) => { invalidate(); throw e; });
    },
    [dropFromActive, invalidateSoon, invalidate]
  );

  // INT-2 (audit): renumber a zone when drag bisection degenerates.
  const handleRenumber = useCallback(
    (gtdList) =>
      apiClient.post('/api/v1/tasks/renumber', { gtd_list: gtdList }).then((r) => {
        const positions = r?.positions || null;
        if (positions) {
          queryClient.setQueryData(['tasks'], (prev) => (
            prev && Array.isArray(prev.tasks)
              ? { ...prev, tasks: prev.tasks.map((t) => (positions[t.id] != null ? { ...t, position: positions[t.id] } : t)) }
              : prev
          ));
        }
        invalidateSoon();
        return positions;
      }),
    [queryClient, invalidateSoon]
  );

  const handleUpdate = useCallback(
    (taskId, updates) => {
      patchTaskCache(taskId, updates);
      return apiClient.put(`/api/v1/tasks/${taskId}`, updates)
        .then((r) => { reconcileRow(r?.task); invalidateSoon(); })
        .catch((e) => { invalidate(); throw e; });
    },
    [patchTaskCache, reconcileRow, invalidateSoon, invalidate]
  );

  const handleDelete = useCallback(
    (taskId) => {
      dropFromActive(taskId);
      return apiClient.delete(`/api/v1/tasks/${taskId}`)
        .then(invalidateSoon)
        .catch((e) => { invalidate(); throw e; });
    },
    [dropFromActive, invalidateSoon, invalidate]
  );

  // Quick-create (card redesign 2026-08-07): the bar's "+ New task", zone
  // footers (gtd_list pre-set) and week columns (due date pre-set) all
  // land here. Capture first (one field, sacred), then place.
  const handleCreate = useCallback(
    ({ title, account, gtd_list, due_date }) => {
      const tmpId = 'tmp-' + Math.random().toString(36).slice(2);
      queryClient.setQueryData(['tasks'], (prev) => (
        prev && Array.isArray(prev.tasks)
          ? {
              ...prev,
              tasks: prev.tasks.concat([{
                id: tmpId, title, status: 'pending', gtd_list: gtd_list || 'inbox',
                triage_state: 'untriaged',
                account_label: account ? String(account).toUpperCase() : null,
                due_date: due_date || null, tags: [],
              }]),
            }
          : prev
      ));
      const swapTemp = (task) => queryClient.setQueryData(['tasks'], (prev) => (
        prev && Array.isArray(prev.tasks)
          ? { ...prev, tasks: task ? prev.tasks.map((t) => (t.id === tmpId ? task : t)) : prev.tasks.filter((t) => t.id !== tmpId) }
          : prev
      ));
      return apiClient.post('/api/v1/capture', { text: title }).then((r) => {
        const id = r?.task?.id;
        const place = {};
        if (account) place.account = account;
        if (gtd_list) place.gtd_list = gtd_list;
        if (due_date !== undefined && due_date !== null) place.due_date = due_date;
        if (id && Object.keys(place).length) {
          return apiClient.put(`/api/v1/tasks/${id}`, place).then((r2) => {
            swapTemp({ ...(r2?.task || r.task), account_label: account ? String(account).toUpperCase() : (r2?.task?.account_label ?? null) });
            invalidateSoon();
          });
        }
        swapTemp(r?.task || null);
        invalidateSoon();
      }).catch((e) => { swapTemp(null); invalidate(); throw e; });
    },
    [queryClient, invalidateSoon, invalidate]
  );

  const bumpContextCount = useCallback((taskId, delta) => {
    queryClient.setQueryData(['tasks'], (prev) => (
      prev && Array.isArray(prev.tasks)
        ? { ...prev, tasks: prev.tasks.map((t) => (t.id === taskId ? { ...t, context_count: Math.max(0, (t.context_count || 0) + delta) } : t)) }
        : prev
    ));
  }, [queryClient]);

  const handleAttach = useCallback(
    (taskId, ctx) => apiClient.post(`/api/v1/tasks/${taskId}/context`, ctx)
      .then(() => { bumpContextCount(taskId, 1); invalidateSoon(); })
      .catch((e) => { invalidate(); throw e; }),
    [bumpContextCount, invalidateSoon, invalidate]
  );

  const handleAttachFile = useCallback(
    (taskId, file) => apiClient.post(`/api/v1/tasks/${taskId}/context/file`, file)
      .then(() => { bumpContextCount(taskId, 1); invalidateSoon(); })
      .catch((e) => { invalidate(); throw e; }),
    [bumpContextCount, invalidateSoon, invalidate]
  );

  const handleGetContextUrl = useCallback(
    (taskId, contextId) =>
      apiClient.get(`/api/v1/tasks/${taskId}/context/${contextId}/url`).then((r) => r?.url || null),
    []
  );

  const handleLoadContext = useCallback(
    (taskId) => apiClient.get(`/api/v1/tasks/${taskId}`).then((r) => r.task?.context || []),
    []
  );

  const handleAssign = useCallback(
    (taskId, person) => {
      if (person?.relation === 'waiting') patchTaskCache(taskId, { waiting_for_who: person.name, gtd_list: 'waiting_for' });
      return apiClient.post(`/api/v1/tasks/${taskId}/assign`, person)
        .then((r) => { reconcileRow(r?.task); invalidateSoon(); })
        .catch((e) => { invalidate(); throw e; });
    },
    [patchTaskCache, reconcileRow, invalidateSoon, invalidate]
  );

  const handleCreatePerson = useCallback(
    (person) =>
      apiClient.post('/api/v1/tasks/people', person).then((r) => {
        queryClient.invalidateQueries({ queryKey: ['task-people'] });
        return r.person;
      }),
    [queryClient]
  );

  const handleRemoveContext = useCallback(
    (taskId, contextId) =>
      apiClient.delete(`/api/v1/tasks/${taskId}/context/${contextId}`)
        .then(() => { bumpContextCount(taskId, -1); invalidateSoon(); })
        .catch((e) => { invalidate(); throw e; }),
    [bumpContextCount, invalidateSoon, invalidate]
  );

  // ONE review surface (audit §5): accept lives in the focus editor +
  // an Accept-all on the Review lens — the old TriageReview strip is gone.
  const handleAcceptTriage = useCallback(
    (taskId) => {
      // Dashed borders clear the moment the user accepts (backend writes
      // triage_state='confirmed').
      patchTaskCache(taskId, { triage_state: 'confirmed' });
      return apiClient.post(`/api/v1/tasks/${taskId}/accept-triage`)
        .then((r) => { reconcileRow(r?.task); invalidateSoon(); })
        .catch((e) => { invalidate(); throw e; });
    },
    [patchTaskCache, reconcileRow, invalidateSoon, invalidate]
  );

  // Flow drill-down: every number on the flow view opens the rows behind
  // it. The component emits a semantic spec; this translates it into task
  // or feed-run queries. Same-spec re-click closes (toggle).
  const [drillSpec, setDrillSpec] = useState(null);
  // All-time carries NO created_after at all, rather than a bound so far in
  // the past it is meaningless — an "everything from this source" drill
  // should not have to justify itself with a 2016 timestamp.
  const windowStartIso = useMemo(
    () => (flowWindow >= ALL_TIME_HOURS
      ? null
      : new Date(Date.now() - flowWindow * 3.6e6).toISOString()),
    [flowWindow],
  );
  const sinceParam = useMemo(
    () => (windowStartIso ? { created_after: windowStartIso } : {}),
    [windowStartIso],
  );
  const drillRequest = useMemo(() => {
    if (!drillSpec) return null;
    const q = { limit: '100' };
    switch (drillSpec.t) {
      case 'source':
        return { kind: 'tasks', params: { ...q, source: drillSpec.source, ...sinceParam } };
      case 'triage':
        return { kind: 'tasks', params: { ...q, status: 'active', triage_state: drillSpec.states } };
      case 'open':
        return { kind: 'tasks', params: { ...q, status: 'active' } };
      case 'list':
        return { kind: 'tasks', params: { ...q, status: 'active', gtd_list: drillSpec.list } };
      case 'account':
        return { kind: 'tasks', params: { ...q, status: 'active', account_id: drillSpec.accountId } };
      case 'waiting':
        return { kind: 'tasks', params: { ...q, status: 'active', gtd_list: 'waiting_for', waiting_for_who: drillSpec.who } };
      case 'goal':
        return { kind: 'tasks', params: { ...q, status: 'active', goal_id: drillSpec.goalId } };
      case 'proactive':
        return { kind: 'tasks', params: { ...q, source: 'intelligence', ...sinceParam } };
      case 'completed':
        return { kind: 'tasks', params: { ...q, status: 'completed',
          ...(windowStartIso ? { completed_after: windowStartIso } : {}) } };
      case 'runs':
        return {
          kind: 'runs',
          params: {
            windowHours: String(flowWindow),
            ...(drillSpec.feed ? { feed: drillSpec.feed } : {}),
            ...(drillSpec.status ? { status: drillSpec.status } : {}),
          },
        };
      default:
        return null;
    }
  }, [drillSpec, sinceParam, windowStartIso, flowWindow]);

  const { data: drillData, isLoading: drillLoading } = useQuery({
    queryKey: ['funnel-drill', drillRequest],
    queryFn: () => (drillRequest.kind === 'runs'
      ? apiClient.get('/api/v1/funnel/runs', { params: drillRequest.params })
      : apiClient.get('/api/v1/tasks', { params: drillRequest.params })),
    enabled: view === 'flow' && !!drillRequest,
    staleTime: 10_000,
  });

  const handleDrill = useCallback((spec) => {
    setDrillSpec((prev) => (
      prev && JSON.stringify(prev) === JSON.stringify(spec) ? null : spec
    ));
  }, []);

  const drill = useMemo(() => {
    if (!drillSpec || !drillRequest) return null;
    return {
      spec: drillSpec,
      loading: drillLoading,
      tasks: drillRequest.kind === 'tasks' ? (drillData?.tasks || []) : undefined,
      runs: drillRequest.kind === 'runs' ? (drillData?.runs || []) : undefined,
    };
  }, [drillSpec, drillRequest, drillLoading, drillData]);

  // Drawer actions reuse the exact same optimistic handlers the boards use,
  // then refresh both the drill list and the flow numbers.
  const refreshDrill = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['funnel-drill'] });
    queryClient.invalidateQueries({ queryKey: ['funnel-flow'] });
  }, [queryClient]);

  const handleDrillAction = useCallback((action, taskId) => {
    const fn = {
      complete: handleComplete,
      reopen: handleReopen,
      accept: handleAcceptTriage,
      archive: handleArchive,
    }[action];
    if (!fn) return undefined;
    return Promise.resolve(fn(taskId)).finally(refreshDrill);
  }, [handleComplete, handleReopen, handleAcceptTriage, handleArchive, refreshDrill]);

  // Drawer quick-add: capture (sacred, one field), then place into the
  // drilled context (waiting-on-X / a GTD list / an account) via the normal
  // update path, then truth-up the drill.
  const handleDrillQuickAdd = useCallback(
    ({ title, gtd_list, waiting_for_who, account_id }) =>
      apiClient.post('/api/v1/capture', { text: title }).then((r) => {
        const id = r?.task?.id;
        const place = {};
        if (gtd_list) place.gtd_list = gtd_list;
        if (waiting_for_who) place.waiting_for_who = waiting_for_who;
        if (account_id) place.account_id = account_id;
        const placed = id && Object.keys(place).length
          ? apiClient.put(`/api/v1/tasks/${id}`, place)
          : Promise.resolve();
        return placed.then(() => { invalidate(); });
      }).catch((e) => { invalidate(); throw e; }),
    [invalidate]
  );

  // Week board meetings overlay (Ryan 2026-09-08): the board hands back its
  // day range (local dates, inclusive); we widen to local midnight→midnight
  // instants and let the board bucket by the viewer's local day. Nothing
  // is cached beyond react-query's window — calendar content is read, not
  // stored. A 400 needsAuth becomes the one-line "connect it" hint.
  const handleLoadMeetings = useCallback(async ({ start, end }) => {
    const startAt = new Date(`${start}T00:00:00`);
    const endAt = new Date(`${end}T23:59:59.999`);
    const qs = new URLSearchParams({
      start: startAt.toISOString(),
      end: endAt.toISOString(),
      maxResults: '250',
    });
    let res;
    try {
      res = await apiClient.get(`/api/calendar/events?${qs}`);
    } catch (e) {
      if (e?.status === 400 || /not connected/i.test(String(e?.message || ''))) {
        throw new Error('Google Calendar isn\'t connected — connect it under Settings › Connections');
      }
      throw new Error(e?.message || 'Calendar unavailable');
    }
    return (res?.events || [])
      .filter((ev) => ev.status !== 'cancelled')
      .map((ev) => ({
        id: ev.id,
        title: ev.title,
        start: ev.start_time,
        end: ev.end_time,
        all_day: !!ev.all_day,
        location: ev.location || null,
        link: ev.hangoutLink || ev.htmlLink || null,
        declined: (ev.attendees || []).some((a) => a.self && a.responseStatus === 'declined'),
      }));
  }, []);

  const handleAcceptAll = useCallback(
    (ids) => {
      (ids || []).forEach((id) => patchTaskCache(id, { triage_state: 'confirmed' }));
      return apiClient.post('/api/v1/tasks/accept-triage', { ids })
        .then(invalidateSoon)
        .catch((e) => { invalidate(); throw e; });
    },
    [patchTaskCache, invalidateSoon, invalidate]
  );

  return (
    <div className="tc-page">
      <div className="tc-page-head">
        <h1 className="tc-page-title">Tasks</h1>
        <div className="tc-view-switcher">
          <button
            className={`tc-view-btn tc-view-btn-icon ${view === 'bubbles' ? 'tc-view-btn-active' : ''}`}
            onClick={() => setView('bubbles')}
            title="Canvas — the bubble playground"
            aria-label="Canvas view"
          >
            <Circle size={15} />
          </button>
          <button
            className={`tc-view-btn tc-view-btn-icon ${view === 'week' ? 'tc-view-btn-active' : ''}`}
            onClick={() => setView('week')}
            title="Week — what needs to get done this week; drop a card on a day to schedule it"
            aria-label="Week view"
          >
            <CalendarRange size={15} />
          </button>
          <button
            className={`tc-view-btn tc-view-btn-icon ${view === 'matrix' ? 'tc-view-btn-active' : ''}`}
            onClick={() => setView('matrix')}
            title="Matrix — the checklist table with exports"
            aria-label="Matrix view"
          >
            <Table2 size={15} />
          </button>
          <button
            className={`tc-view-btn tc-view-btn-icon ${view === 'flow' ? 'tc-view-btn-active' : ''}`}
            onClick={() => setView('flow')}
            title="Flow — every input stream, the triage cauldron, and where work routes"
            aria-label="Flow view"
          >
            <Waves size={15} />
          </button>
          <button
            className={`tc-view-btn tc-view-btn-icon ${view === 'number' ? 'tc-view-btn-active' : ''}`}
            onClick={() => setView('number')}
            title="The Number — quota thermometer + deal playground"
            aria-label="The Number"
          >
            <Gauge size={15} />
          </button>
          <button
            className={`tc-view-btn tc-view-btn-icon ${view === 'review' ? 'tc-view-btn-active' : ''}`}
            onClick={() => setView('review')}
            title="Review — everything that needs you, one queue, keyboard-speed"
            aria-label="Review queue"
            style={{ position: 'relative' }}
          >
            <Inbox size={15} />
            {(reviewData?.counts?.total || 0) > 0 && (
              <span className="tc-review-badge">{reviewData.counts.total > 99 ? '99+' : reviewData.counts.total}</span>
            )}
          </button>
          <button
            className={`tc-view-btn tc-view-btn-icon ${view === 'goals' ? 'tc-view-btn-active' : ''}`}
            onClick={() => setView('goals')}
            title="Goals — outcomes, plans, and the proactive-time share"
            aria-label="Goals view"
          >
            <Target size={15} />
          </button>
          <button
            className={`tc-view-btn tc-view-btn-icon ${showDone ? 'tc-view-btn-active' : ''}`}
            onClick={() => setShowDone(!showDone)}
            title="History — include completed tasks (struck through, searchable, reopenable)"
            aria-label="Toggle completed history"
          >
            <History size={15} />
          </button>
        </div>
      </div>

      <TodayBar
        data={todayData || null}
        loading={todayLoading}
        tasks={taskData?.tasks || []}
        onSetToday={handleSetToday}
        onComplete={handleComplete}
        onFocusChange={setFocusActive}
      />

      <TaskFilterBar
        tasks={tasks}
        accounts={accountOptions}
        value={filter}
        onChange={setFilter}
        onAcceptAll={handleAcceptAll}
        onCreate={handleCreate}
        createHint="What needs doing? Enter saves · Cmd+Shift+K captures from anywhere"
      />

      {/* R1 (one review surface): the GroupCandidates strip that used to sit
          here moved into the Review queue — proposals must not colonize the
          working canvas (UX review 2026-08-17). ApprovalQueue stays for now:
          approvals gate executions, and until the Review queue shows the
          draft body it remains the only place to read what you'd approve. */}
      <ApprovalQueue />

      {/* The cap note describes the task LIST the bubbles/week/matrix views
          render — on goals, number, flow and review it is only noise above
          a board that reads its own data (Ryan 2026-09-08). */}
      {capped && LIST_VIEWS.has(view) && (
        <div className="tc-cap-note" role="status">
          Showing the first {fetchedActive} of {activeTotal} open tasks — narrow a filter, or archive what's stale.
        </div>
      )}

      {view === 'bubbles' ? (
        <TaskBubbleCanvas
          tasks={canvasTasks}
          filter={filter}
          onFilterChange={setFilter}
          hideFilterBar
          onLogEvents={handleLogEvents}
          onReframe={handleReframe}
          onAcceptTriage={handleAcceptTriage}
          onMove={handleMove}
          onComplete={handleComplete}
          onReopen={handleReopen}
          onGroup={handleGroup}
          onRenumber={handleRenumber}
          onUpdate={handleUpdate}
          onAttach={handleAttach}
          onAttachFile={handleAttachFile}
          onGetContextUrl={handleGetContextUrl}
          onLoadContext={handleLoadContext}
          onRemoveContext={handleRemoveContext}
          onDelete={handleDelete}
          onCreate={handleCreate}
          onArchive={handleArchive}
          onBulkArchive={handleBulkArchive}
          onUnarchive={handleUnarchive}
          accounts={accountOptions}
          people={people}
          onAssign={handleAssign}
          onCreatePerson={handleCreatePerson}
        />
      ) : view === 'week' ? (
        <TaskWeekBoard
          tasks={filtered}
          onLoadMeetings={handleLoadMeetings}
          onLogEvents={handleLogEvents}
          onReframe={handleReframe}
          onAcceptTriage={handleAcceptTriage}
          onComplete={handleComplete}
          onReopen={handleReopen}
          onUpdate={handleUpdate}
          onAttach={handleAttach}
          onAttachFile={handleAttachFile}
          onGetContextUrl={handleGetContextUrl}
          onLoadContext={handleLoadContext}
          onRemoveContext={handleRemoveContext}
          onDelete={handleDelete}
          onCreate={handleCreate}
          onArchive={handleArchive}
          onBulkArchive={handleBulkArchive}
          onUnarchive={handleUnarchive}
          onAssign={handleAssign}
          onCreatePerson={handleCreatePerson}
          accounts={accountOptions}
          people={people}
        />
      ) : view === 'flow' ? (
        <TaskFlow
          data={flowData || null}
          loading={flowLoading}
          onWindowChange={setFlowWindow}
          drill={drill}
          onDrill={handleDrill}
          onDrillClose={() => setDrillSpec(null)}
          onTaskAction={handleDrillAction}
          onQuickAdd={handleDrillQuickAdd}
          onUpdate={handleUpdate}
          onReframe={handleReframe}
          onAttach={handleAttach}
          onAttachFile={handleAttachFile}
          onGetContextUrl={handleGetContextUrl}
          onLoadContext={handleLoadContext}
          onRemoveContext={handleRemoveContext}
          onDelete={handleDelete}
          onAssign={handleAssign}
          onCreatePerson={handleCreatePerson}
          accounts={accountOptions}
          people={people}
          connectors={isAdmin ? connectorMap : null}
          onConnectorOpen={isAdmin ? handleConnectorOpen : undefined}
          onConnectorTest={isAdmin ? handleConnectorTest : undefined}
          onConnectorConnect={isAdmin ? handleConnectorConnect : undefined}
          streamCatalog={isAdmin ? streamCatalog : null}
          onOpenGoals={() => setView('goals')}
        />
      ) : view === 'number' ? (
        <>
          {pipelineFailed && (
            <div className="tc-load-error" role="alert">
              <span>The Number failed to load — the thermometer below is not your data.</span>
              <button type="button" onClick={() => refetchPipeline()}>Retry</button>
            </div>
          )}
          <PipelinePlayground
            data={pipelineData || null}
            loading={pipelineLoading}
            accounts={accountOptions}
            goals={(goalsData?.goals || []).filter((g) => g.status === 'active')}
            onSelectFy={setNumberFy}
            onSetQuota={handleSetQuota}
            onCreateDeal={handleCreateDeal}
            onUpdateDeal={handleUpdateDeal}
            onDeleteDeal={handleDeleteDeal}
          />
        </>
      ) : view === 'review' ? (
        <ReviewQueue
          data={reviewData || null}
          loading={reviewLoading}
          {...reviewHandlers}
        />
      ) : view === 'goals' ? (
        <>
          {/* A failed load must never masquerade as an empty whiteboard
              (adversarial review §1.2 — the outage was invisible). */}
          {(goalsFailed || syncsFailed) && (
            <div className="tc-load-error" role="alert">
              <span>
                {goalsFailed
                  ? 'Goals failed to load — the board below is NOT your data.'
                  : 'Recurring syncs failed to load — the rhythms section is incomplete.'}
              </span>
              <button
                type="button"
                onClick={() => { if (goalsFailed) refetchGoals(); if (syncsFailed) refetchSyncs(); }}
              >
                Retry
              </button>
            </div>
          )}
          <GoalsPanel
            goals={goalsData?.goals || []}
            allocation={allocationData || null}
            loading={goalsLoading}
            tasks={tasks}
            accounts={accountOptions}
            onCreate={handleCreateGoal}
            onMoveMany={handleMoveMany}
            boards={boards}
            currentBoardId={activeBoardId}
            onOpenBoard={openBoard}
            onCreateBoard={createBoard}
            onRenameBoard={renameBoard}
            onRetagBoard={retagBoard}
            onArchiveBoard={archiveBoard}
            objects={canvasData?.objects || []}
            edges={canvasData?.edges || []}
            onCreateObject={handleCreateObject}
            onUpdateObject={handleUpdateObject}
            onDeleteObject={handleDeleteObject}
            onCreateEdge={handleCreateEdge}
            onUpdateEdge={handleUpdateEdge}
            onDeleteEdge={handleDeleteEdge}
            onPolishRegion={polishRegion}
            onProcessRegion={processRegion}
            onCommitRegion={commitRegion}
            onImportPhoto={importPhoto}
            onReadPhoto={readPhoto}
            onCommitStickies={commitStickies}
            onUpdateStatus={handleUpdateGoalStatus}
            onUpdateGoal={handleUpdateGoal}
            notes={notesData?.notes || []}
            onCreateNote={handleCreateNote}
            onUpdateNote={handleUpdateNote}
            onDeleteNote={handleDeleteNote}
            onDecomposeGoal={handleDecomposeGoal}
            onDecompose={handleDecompose}
            coverage={coverageData?.targets || []}
            people={people}
            onAddCoverage={handleAddCoverage}
            onUpdateCoverage={handleUpdateCoverage}
            syncs={syncsData?.syncs || []}
            onAddSync={handleAddSync}
            onUpdateSync={handleUpdateSync}
            pursuits={pursuitsData?.pursuits || []}
            onAddPursuit={handleAddPursuit}
            onUpdatePursuit={handleUpdatePursuit}
            pipeline={pipelineData || null}
            onOpenNumber={() => setView('number')}
            onSuggestWalkBack={handleSuggestWalkBack}
            onUpdateDeal={handleUpdateDeal}
          />
        </>
      ) : (
        <TaskMatrix
          tasks={filtered}
          people={people}
          onComplete={handleComplete}
          onReopen={handleReopen}
          onUpdate={handleUpdate}
          onArchive={handleArchive}
          onBulkArchive={handleBulkArchive}
          onDelete={handleDelete}
          onAssign={handleAssign}
          onLogEvents={handleLogEvents}
        />
      )}
    </div>
  );
}

export default TasksPage;
